/* =========================================================================
   Telemetry RegEx & Dual-Domain (Time / Parameter) Search Engine
   Provides safe RegExp matching with plain-text fallback,
   filtering both table parameter columns and timestamp rows.
   ========================================================================= */

import { tableColLabel } from './telemetry-helpers.utils';

/**
 * Creates a pre-compiled, high-performance query matcher function.
 * Compiles the RegExp only ONCE per search query instead of inside every loop iteration.
 */
export function createSearchMatcher(query?: string | null): (target: string | null | undefined) => boolean {
  if (!query || !query.trim()) {
    return () => true;
  }
  const q = query.trim();
  const qLower = q.toLowerCase();

  try {
    const re = new RegExp(q, 'i');
    return (target: string | null | undefined) => {
      if (target == null) return false;
      const str = String(target).trim();
      return re.test(str);
    };
  } catch (_) {
    // Incomplete regex syntax fallback (e.g. "[" or "(")
    return (target: string | null | undefined) => {
      if (target == null) return false;
      return String(target).toLowerCase().includes(qLower);
    };
  }
}

/**
 * Safe RegEx and case-insensitive substring matcher (convenience wrapper).
 */
export function matchQueryOrRegex(target: string | null | undefined, query?: string | null): boolean {
  return createSearchMatcher(query)(target);
}

/**
 * Filter virtualized table columns by parameter name, label, ID, or subsystem.
 * If the search query specifically matches timestamp formats and NO columns match,
 * keeps all columns visible so the user can inspect all parameters for that timestamp.
 */
export function filterTableColumnsByQuery(columns: any[], query?: string | null): any[] {
  if (!query || !query.trim() || !Array.isArray(columns) || columns.length === 0) {
    return columns;
  }

  const matches = createSearchMatcher(query);

  const matched = columns.filter((c) => {
    const lbl = tableColLabel(c);
    const sub = c.subsystem || c.sub || '';
    const pid = c.paramId || c.paramName || c.tel || '';
    const uid = c.uniqueId || '';

    return matches(lbl) || matches(sub) || matches(pid) || matches(uid);
  });

  // If query matched zero columns, check if it might be a timestamp query
  if (matched.length === 0) {
    return columns;
  }

  return matched;
}

/**
 * Filter virtualized table timestamps by formatted clock time or ISO date.
 * If query matches one or more timestamps, returns filtered rows.
 * If query does not match any timestamps (e.g. user is searching parameter names),
 * returns all timestamps so the parameter's history is visible.
 */
export function filterTableTimestampsByQuery(
  timestamps: number[],
  query?: string | null,
  fmtClockFn?: (ts: number) => string
): number[] {
  if (!query || !query.trim() || !Array.isArray(timestamps) || timestamps.length === 0) {
    return timestamps;
  }

  const matches = createSearchMatcher(query);

  const matched = timestamps.filter((t) => {
    const clockStr = fmtClockFn ? fmtClockFn(t) : '';
    let isoStr = '';
    try {
      isoStr = new Date(t).toISOString();
    } catch (_) { }

    return matches(clockStr) || matches(isoStr);
  });

  if (matched.length > 0) {
    return matched;
  }

  return timestamps;
}
